// hooks/features/sites/useSites.ts

import { useState, useEffect, useCallback } from "react";
import useToken from "../auth/useToken";
import { siteService } from "../../../services/siteService";
import { Site, SiteFormData } from "../../../types";
import { uploadService } from "../../../services/uploadService";
import useAuth from "../auth/useAuth";

export default function useSites() {
  const { token } = useToken();
  const { user } = useAuth();
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    total: 0,
    perPage: 20,
  });

  // 🔓 PUBLIC - Pas de token nécessaire
  const fetchSites = useCallback(async (page: number = 1, perPage: number = 20, search?: string, isActive?: boolean) => {
    setLoading(true);
    setError(null);
    try {
      const response = await siteService.getAll(page, perPage, search, isActive);
      setSites(response.data);
      setPagination({
        currentPage: response.current_page,
        lastPage: response.last_page,
        total: response.total,
        perPage: response.per_page,
      });
    } catch (err) {
      setError("Erreur lors du chargement des sites");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // 🔓 PUBLIC - Pas de token nécessaire
  const fetchSiteById = useCallback(async (id: string): Promise<Site | null> => {
    setLoading(true);
    setError(null);
    try {
      const site = await siteService.getById(id);
      return site;
    } catch (err) {
      setError("Erreur lors du chargement du site");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // 🔓 PUBLIC - Pas de token nécessaire
  const fetchSiteBySlug = useCallback(async (slug: string): Promise<Site | null> => {
    setLoading(true);
    setError(null);
    try {
      const site = await siteService.getBySlug(slug);
      return site;
    } catch (err) {
      setError("Erreur lors du chargement du site par slug");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // 🔒 PROTÉGÉ - Token requis
  const createSite = useCallback(async (data: SiteFormData): Promise<Site | null> => {
    if (!token) {
      setError("Vous devez être connecté");
      return null;
    }

    setLoading(true);
    setError(null);
    try {
      // 🔥 Upload des fichiers avant de créer le site
      let logoUrl = data.logo || null;
      let faviconUrl = data.favicon || null;

      // Upload du logo
      if (data.logoFile) {
        const response = await uploadService.uploadFile(token, {
          type: "image",
          file: data.logoFile,
          reason: "Logo du site",
          user_id: user?.id || "",
        });
        logoUrl = response.url;
      }

      // Upload du favicon
      if (data.faviconFile) {
        const response = await uploadService.uploadFile(token, {
          type: "image",
          file: data.faviconFile,
          reason: "Favicon du site",
          user_id: user?.id || "",
        });
        faviconUrl = response.url;
      }

      // Préparer les données finales
      const finalData: SiteFormData = {
        ...data,
        logo: logoUrl,
        favicon: faviconUrl,
        logoFile: null,
        faviconFile: null,
      };

      const newSite = await siteService.create(token, finalData);
      setSites((prev) => [newSite, ...prev]);
      return newSite;
    } catch (err) {
      setError("Erreur lors de la création du site");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token, user]);

  // 🔒 PROTÉGÉ - Token requis
  const updateSite = useCallback(async (id: string, data: Partial<SiteFormData>): Promise<Site | null> => {
    if (!token) {
      setError("Vous devez être connecté");
      return null;
    }

    setLoading(true);
    setError(null);
    try {
      // 🔥 Upload des fichiers avant la mise à jour
      let logoUrl = data.logo || null;
      let faviconUrl = data.favicon || null;

      // Upload du logo
      if (data.logoFile) {
        const response = await uploadService.uploadFile(token, {
          type: "image",
          file: data.logoFile,
          reason: "Logo du site (mise à jour)",
          user_id: user?.id || "",
        });
        logoUrl = response.url;
      }

      // Upload du favicon
      if (data.faviconFile) {
        const response = await uploadService.uploadFile(token, {
          type: "image",
          file: data.faviconFile,
          reason: "Favicon du site (mise à jour)",
          user_id: user?.id || "",
        });
        faviconUrl = response.url;
      }

      const finalData = {
        ...data,
        logo: logoUrl,
        favicon: faviconUrl,
        logoFile: null,
        faviconFile: null,
      };

      const updatedSite = await siteService.update(token, id, finalData);
      setSites((prev) => prev.map((s) => (s.id === id ? updatedSite : s)));
      return updatedSite;
    } catch (err) {
      setError("Erreur lors de la mise à jour du site");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token, user]);

  // 🔒 PROTÉGÉ - Token requis
  const deleteSite = useCallback(async (id: string): Promise<boolean> => {
    if (!token) {
      setError("Vous devez être connecté");
      return false;
    }

    setLoading(true);
    setError(null);
    try {
      await siteService.delete(token, id);
      setSites((prev) => prev.filter((s) => s.id !== id));
      return true;
    } catch (err) {
      setError("Erreur lors de la suppression du site");
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 🔒 PROTÉGÉ - Token requis
  const deleteManySites = useCallback(async (ids: string[]): Promise<boolean> => {
    if (!token) {
      setError("Vous devez être connecté");
      return false;
    }

    setLoading(true);
    setError(null);
    try {
      await siteService.deleteMany(token, ids);
      setSites((prev) => prev.filter((s) => !ids.includes(s.id)));
      return true;
    } catch (err) {
      setError("Erreur lors de la suppression multiple");
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 🔒 PROTÉGÉ - Token requis
  const toggleActive = useCallback(async (id: string, currentStatus: boolean): Promise<boolean> => {
    if (!token) {
      setError("Vous devez être connecté");
      return false;
    }

    setLoading(true);
    try {
      const updated = await siteService.update(token, id, { is_active: !currentStatus });
      setSites((prev) => prev.map((s) => (s.id === id ? updated : s)));
      return true;
    } catch (err) {
      setError("Erreur lors du changement de statut");
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchSites();
  }, [fetchSites]);

  return {
    sites,
    loading,
    error,
    pagination,
    fetchSites,
    fetchSiteById,
    fetchSiteBySlug,
    createSite,
    updateSite,
    deleteSite,
    deleteManySites,
    toggleActive,
  };
}